import { resetCalibrationState } from './calibration/resetCalibrationState'
import { generateInitialProtocol, connectionErrorMessage } from './generateInitialProtocol'
import { countTrainingSchedules, protocolWasPersisted } from './protocolIntegrity'
import type { ProfileRow } from './profile'
import { captureError } from './sentry'

export type EnsureProtocolReadyResult = {
  ok: boolean
  error: string | null
  regenerated: boolean
}

/**
 * Ensures training schedules and meals exist. Resets calibration and regenerates
 * the protocol when onboarding completed without a persisted programme.
 */
export async function ensureProtocolReady(
  userId: string,
  profile: ProfileRow,
): Promise<EnsureProtocolReadyResult> {
  if (await protocolWasPersisted(profile.weekly_commitment)) {
    return { ok: true, error: null, regenerated: false }
  }

  captureError(new Error('Missing persisted protocol for onboarded user'), {
    action: 'ensure_protocol_ready',
    userId,
    scheduleCount: await countTrainingSchedules(),
  })

  const { error: resetError } = await resetCalibrationState(userId)
  if (resetError) {
    return { ok: false, error: resetError, regenerated: false }
  }

  const { error: genError } = await generateInitialProtocol(userId, profile)
  if (genError) {
    return { ok: false, error: genError, regenerated: false }
  }

  if (!(await protocolWasPersisted(profile.weekly_commitment))) {
    return { ok: false, error: connectionErrorMessage(), regenerated: false }
  }

  return { ok: true, error: null, regenerated: true }
}
